<script setup lang="ts">
import { computed, ref, watch, type Ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { BuildingIcon, CheckIcon, ChevronDownIcon } from 'lucide-vue-next'
import InputText from '@/components/ui/InputText.vue'
import Popover from '@/components/ui/Popover.vue'
import { useApi } from '@/composables/useApi'
import type { Company } from '@/interfaces/company'
import { useAppStore } from '@/stores/app'

const { t } = useI18n()
const router = useRouter()
const appStore = useAppStore()
const api = useApi()

const companies: Ref<Company[]> = ref([])
const query = ref('')
const popover = ref<InstanceType<typeof Popover> | null>(null)
const trigger = ref<HTMLElement | null>(null)

// Only the user's own companies: admins can read every company through
// /api/companies, but work inside the ones they are a member of.
api
  .get('user/me/companies')
  .then(async (response) => {
    companies.value = await response.json<Company[]>()
    if (companies.value.length == 1) {
      appStore.selectCompany(companies.value[0])
    }
  })
  .catch(() => {
    // Fallita la lettura delle aziende: si passa comunque allo stato "nessuna azienda",
    // non si resta bloccati sullo stato di caricamento.
  })
  .finally(() => {
    appStore.setCompaniesLoaded(true)
  })

const canSwitch = computed(() => appStore.companiesLoaded && companies.value.length !== 1)

const filtered = computed(() => {
  const needle = query.value.trim().toLowerCase()
  if (!needle) return companies.value
  return companies.value.filter((company) => (company.name ?? '').toLowerCase().includes(needle))
})

const toggle = (event: Event) => {
  query.value = ''
  popover.value?.toggle(event, trigger.value)
}

const select = (company: Company) => {
  appStore.selectCompany(company)
  popover.value?.hide()
  router.push({ name: 'home' })
}

watch(
  () => appStore.companySwitcherRequest,
  () => {
    if (!canSwitch.value) return
    query.value = ''
    // The request usually comes from a click elsewhere (the "no company" button). The browser
    // drains microtasks between listeners, so opening right away binds the Popover's outside-click
    // listener before that same click bubbles to document, which then closes it at once.
    // Open on the next task, after the click has finished propagating.
    setTimeout(() => {
      // A store-requested open has no real click event, but PrimeVue's Popover.show()
      // reads event.currentTarget as the outside-click origin: pass the trigger element as a stand-in.
      popover.value?.show({ currentTarget: trigger.value } as unknown as Event, trigger.value)
    })
  }
)
</script>

<template>
  <div class="flex min-w-0 items-center">
    <button
      v-if="canSwitch"
      ref="trigger"
      type="button"
      data-test="company-switcher-trigger"
      class="flex h-8 min-w-0 max-w-64 items-center gap-2 rounded-md border border-surface-200 px-2.5 text-sm text-surface-700 transition-colors hover:bg-surface-100 dark:border-surface-700 dark:text-surface-200 dark:hover:bg-surface-800"
      :aria-label="t('layout.companySwitcher.button')"
      aria-haspopup="listbox"
      @click="toggle"
    >
      <BuildingIcon class="size-4 shrink-0 text-surface-500" aria-hidden="true" />
      <span class="truncate">{{ appStore.currentCompany?.name ?? t('layout.companySwitcher.button') }}</span>
      <ChevronDownIcon class="size-4 shrink-0 text-surface-500" aria-hidden="true" />
    </button>
    <span
      v-else
      ref="trigger"
      data-test="company-name"
      class="flex h-8 min-w-0 items-center gap-2 px-1 text-sm font-medium text-surface-700 dark:text-surface-200"
    >
      <BuildingIcon class="size-4 shrink-0 text-surface-500" aria-hidden="true" />
      <span class="truncate">{{ appStore.currentCompany?.name }}</span>
    </span>

    <Popover ref="popover">
      <div class="w-72 space-y-2">
        <InputText
          v-model="query"
          data-test="company-search"
          class="w-full"
          :placeholder="t('layout.companySwitcher.search')"
          :aria-label="t('layout.companySwitcher.search')"
        />
        <ul class="max-h-80 space-y-0.5 overflow-y-auto" role="listbox">
          <li v-for="company in filtered" :key="company.id">
            <button
              type="button"
              role="option"
              data-test="company-option"
              :aria-selected="company.id === appStore.currentCompany?.id"
              class="flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-sm text-surface-700 hover:bg-surface-100 dark:text-surface-200 dark:hover:bg-surface-800"
              @click="select(company)"
            >
              <span class="flex-1 truncate">{{ company.name }}</span>
              <CheckIcon
                v-if="company.id === appStore.currentCompany?.id"
                class="size-4 shrink-0 text-primary"
                aria-hidden="true"
              />
            </button>
          </li>
          <li v-if="filtered.length === 0" class="px-2 py-1.5 text-sm text-surface-500">
            {{ t('layout.companySwitcher.empty') }}
          </li>
        </ul>
      </div>
    </Popover>
  </div>
</template>
